import { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

import {
  ArrowRight,
  ArrowUpRight,
  Boxes,
  Coins,
  Network,
  Package,
  Sparkles,
  Store,
  Target,
  Truck,
} from "lucide-react";

import { Default } from "../../components/Default/Default";
import { Copilot, LoadingContent } from "../../components/SharedComponents/sharedComponents.index";
import { LOADING_CONTENT_TYPE } from "../../components/SharedComponents/LoadingContent/loading.content.constants";
import { SYSTEM_COLORS } from "../../components/system.colors";

import {
  useHomeViewModel,
  formatCompactCurrency,
  formatFullCurrency,
  formatPercent,
} from "../../viewModel/useHomeViewModel";

const ACCENT = '#e0623a';
const ACCENT_SOFT = '#fdeee8';
const POSITIVE = { color: '#059669', bg: '#e6f7f1' };
const NEGATIVE = { color: '#e11d48', bg: '#ffe4ea' };
const QUALITY_BAR = '#cfcfcf';

const SEVERITY_COLORS = {
  high: '#f43f5e',
  medium: '#f59e0b',
  low: '#eab308',
};

const MODULE_ICONS = {
  product: Package,
  assortment: Network,
  inventory: Truck,
  channels: Store,
};

const cardStyle = {
  background: SYSTEM_COLORS.WHITE.WHITE,
  border: `1px solid ${SYSTEM_COLORS.GRAY.GRAY_200}`,
  borderRadius: 20,
  boxShadow: `0 1px 3px ${SYSTEM_COLORS.BLACK.BLACK_8_PERCENT}`,
};

const sectionTitleStyle = { fontSize: '1.125rem', fontWeight: 700, color: SYSTEM_COLORS.BLACK.DARK_NAVY, margin: 0 };
const sectionSubtitleStyle = { fontSize: '0.8rem', color: SYSTEM_COLORS.GRAY.MEDIUM_GRAY, margin: 0 };

const blurredStyle = { filter: 'blur(3px)', pointerEvents: 'none', userSelect: 'none' };

const ComingSoonBadge = () => (
  <span style={{
    position: 'absolute', insetBlockStart: '50%', insetInlineStart: '50%', transform: 'translate(-50%, -50%)',
    padding: '4px 12px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 700,
    color: SYSTEM_COLORS.GRAY.CHARCOAL_GRAY, background: SYSTEM_COLORS.WHITE.WHITE,
    border: `1px solid ${SYSTEM_COLORS.GRAY.GRAY_200}`, boxShadow: `0 1px 3px ${SYSTEM_COLORS.BLACK.BLACK_8_PERCENT}` }}>
    Em breve
  </span>
);

const EarningsTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const { monthlyReturn, opportunityQuality } = payload[0].payload;

  return (
    <div style={{
      background: SYSTEM_COLORS.WHITE.WHITE, border: `1px solid ${SYSTEM_COLORS.GRAY.GRAY_200}`, borderRadius: 12,
      padding: '10px 14px', fontSize: '0.8rem', display: 'grid', gap: 8,
      boxShadow: `0 4px 12px ${SYSTEM_COLORS.BLACK.BLACK_8_PERCENT}` }}>
      <span style={{ color: SYSTEM_COLORS.BLACK.EERIE_BLACK }}>{label}</span>
      <span style={{ color: SYSTEM_COLORS.BLACK.EERIE_BLACK }}>
        Qualidade das execuções : {formatPercent(opportunityQuality)}
      </span>
      <span style={{ color: ACCENT }}>Retorno acumulado : {formatFullCurrency(monthlyReturn)}</span>
    </div>
  );
};

export function HomeScreen() {
  const navigate = useNavigate();
  const vm = useHomeViewModel();

  const [hoveredId, setHoveredId] = useState(null);
  const [selectedCardKey, setSelectedCardKey] = useState(null);

  const toggleSelectedCard = (key) => setSelectedCardKey(prev => (prev === key ? null : key));

  const {
    greeting,
    summary,
    earningsChart,
    expectedReturn,
    moduleCards,
    recommendations,
    productWithGreatestOpportunityValue,
    userId,
    tenantId,
  } = vm.ctx;

  if (vm.uiState.isLoadingHome)
    return <LoadingContent label={"Carregando sua home..."} type={LOADING_CONTENT_TYPE.LOCAL_PAGE_STATE} />;

  const availableModulesCount = moduleCards.filter(card => card.isAvailable).length;
  const selectedCard = moduleCards.find(card => card.key === selectedCardKey);

  return (
    <Default>
      <section style={{
        display: 'flex', flexDirection: 'column', gap: 32, paddingBlock: '2rem 3rem',
        marginInlineStart: 'var(--menu-width-collapsed)' }}>

        {/* Saudação */}
        <header style={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: 16 }}>
          <div style={{ display: 'grid', gap: 4 }}>
            <h1 style={{ fontSize: '1.6rem', fontWeight: 800, color: SYSTEM_COLORS.BLACK.DARK_NAVY, margin: 0 }}>
              {greeting}
            </h1>
            <p style={sectionSubtitleStyle}>
              A IA da Aidron mapeou {summary.totalOfOpportunities} oportunidades
              em {availableModulesCount} {availableModulesCount === 1 ? 'módulo' : 'módulos'}
              {' — '}{formatCompactCurrency(summary.openValue)} ainda em aberto
            </p>
          </div>

          <Copilot ctx={{ userId, tenantId }} />
        </header>

        {vm.uiState.homeError && (
          <p style={{ ...sectionSubtitleStyle, color: NEGATIVE.color }}>
            Não foi possível carregar os dados da home. Tente novamente em instantes.
          </p>
        )}

        {/* Retorno */}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(280px, 1fr)', gap: 20 }}>
          <article style={{ ...cardStyle, padding: '24px 24px 16px', display: 'grid', gap: 8, minInlineSize: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ display: 'grid', gap: 4 }}>
                <h2 style={{ ...sectionTitleStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Coins size={16} color={ACCENT} /> Retorno acumulado dos últimos 6 meses
                </h2>
                <p style={sectionSubtitleStyle}>Retorno acumulado x qualidade das oportunidades executadas</p>
              </div>

              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 999, whiteSpace: 'nowrap',
                fontSize: '0.8rem', fontWeight: 800,
                color: earningsChart.semesterVariation >= 0 ? POSITIVE.color : NEGATIVE.color,
                background: earningsChart.semesterVariation >= 0 ? POSITIVE.bg : NEGATIVE.bg }}>
                <ArrowUpRight size={14} style={{ transform: earningsChart.semesterVariation >= 0 ? 'none' : 'rotate(90deg)' }} />
                {earningsChart.semesterVariation >= 0 ? '+' : '-'}
                {formatPercent(Math.abs(earningsChart.semesterVariation), { decimals: 1 })} vs. semestre anterior
              </span>
            </div>

            <div style={{ display: 'flex', gap: 18, fontSize: '0.75rem', fontWeight: 600, color: SYSTEM_COLORS.GRAY.MEDIUM_GRAY }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span style={{ inlineSize: 18, blockSize: 6, borderRadius: 4, background: ACCENT }} /> Retorno acumulado
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span style={{ inlineSize: 10, blockSize: 10, borderRadius: '50%', background: QUALITY_BAR }} /> Qualidade das oportunidades executadas
              </span>
            </div>

            <div style={{ blockSize: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={earningsChart.data} margin={{ top: 8, right: 0, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="homeReturnGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={ACCENT} stopOpacity={0.18} />
                      <stop offset="100%" stopColor={ACCENT} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} strokeDasharray="4 4" stroke={SYSTEM_COLORS.GRAY.GRAY_200} />
                  <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: SYSTEM_COLORS.GRAY.MEDIUM_GRAY }} />
                  <YAxis
                    yAxisId="return"
                    axisLine={false}
                    tickLine={false}
                    width={48}
                    tick={{ fontSize: 12, fill: SYSTEM_COLORS.GRAY.MEDIUM_GRAY }}
                    tickFormatter={(value) => `${value / 1_000_000}M`}
                  />
                  <YAxis
                    yAxisId="quality"
                    orientation="right"
                    domain={[0, 1]}
                    ticks={[0, 0.25, 0.5, 0.75, 1]}
                    axisLine={false}
                    tickLine={false}
                    width={48}
                    tick={{ fontSize: 12, fill: SYSTEM_COLORS.GRAY.MEDIUM_GRAY }}
                    tickFormatter={(value) => formatPercent(value)}
                  />
                  <Tooltip content={<EarningsTooltip />} cursor={{ stroke: SYSTEM_COLORS.GRAY.BLUE_GRAY, strokeWidth: 1 }} />
                  <Bar yAxisId="quality" dataKey="opportunityQuality" barSize={26} radius={[6, 6, 0, 0]} fill={QUALITY_BAR} />
                  <Area
                    yAxisId="return"
                    type="monotone"
                    dataKey="monthlyReturn"
                    stroke={ACCENT}
                    strokeWidth={2}
                    fill="url(#homeReturnGradient)"
                    activeDot={{ r: 5, fill: ACCENT, stroke: SYSTEM_COLORS.WHITE.WHITE, strokeWidth: 2 }}
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </article>

          <article style={{
            ...cardStyle, border: 'none', position: 'relative', overflow: 'hidden', padding: 24,
            display: 'flex', flexDirection: 'column', gap: 16, color: SYSTEM_COLORS.WHITE.WHITE,
            background: `linear-gradient(135deg, ${ACCENT} 0%, #e58a6a 100%)` }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.06em' }}>
              <Target size={16} /> RETORNO ESPERADO TOTAL
            </span>
            <strong style={{ fontSize: '2rem', fontWeight: 800, lineHeight: 1 }}>
              {formatFullCurrency(expectedReturn.total)}
            </strong>
            <div style={{ blockSize: 8, borderRadius: 999, background: 'rgba(255,255,255,0.3)' }}>
              <div style={{
                blockSize: '100%', borderRadius: 999, background: SYSTEM_COLORS.WHITE.WHITE,
                inlineSize: `${Math.min(expectedReturn.capturedPercent, 1) * 100}%` }} />
            </div>
            <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>
              {formatPercent(expectedReturn.capturedPercent)} já capturado · {formatCompactCurrency(expectedReturn.capturedValue)} nos últimos 6 meses
            </span>
            <Boxes
              size={120}
              strokeWidth={1.5}
              style={{ position: 'absolute', insetInlineEnd: -16, insetBlockEnd: -24, opacity: 0.18 }} />
          </article>
        </div>

        {/* Oportunidades por módulo */}
        <section style={{ display: 'grid', gap: 16 }}>
          <div style={{ display: 'grid', gap: 4 }}>
            <h2 style={sectionTitleStyle}>Oportunidades por módulo</h2>
            <p style={sectionSubtitleStyle}>Clique em um módulo para ver os destaques e ir direto às ações recomendadas</p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20 }}>
            {moduleCards.map(card => {
              const Icon = MODULE_ICONS[card.key] ?? Package;

              return (
                <article
                  key={card.key}
                  role={card.isAvailable ? 'button' : undefined}
                  tabIndex={card.isAvailable ? 0 : -1}
                  onClick={() => card.isAvailable && toggleSelectedCard(card.key)}
                  onKeyDown={(e) => card.isAvailable && e.key === 'Enter' && toggleSelectedCard(card.key)}
                  onMouseEnter={() => card.isAvailable && setHoveredId(card.key)}
                  onMouseLeave={() => setHoveredId(null)}
                  style={{
                    ...cardStyle, position: 'relative', padding: 20, cursor: card.isAvailable ? 'pointer' : 'default',
                    transition: 'transform .2s ease, box-shadow .2s ease, border-color .2s ease',
                    ...(hoveredId === card.key && {
                      transform: 'translateY(-2px)', boxShadow: `0 6px 16px ${SYSTEM_COLORS.BLACK.BLACK_8_PERCENT}` }),
                    ...(selectedCardKey === card.key && {
                      border: `1px solid ${ACCENT}`, boxShadow: '0 0 0 1px rgba(224,98,58,0.35)' }) }}>
                  <div style={{ display: 'grid', gap: 12, ...(card.isAvailable ? {} : blurredStyle) }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{
                        display: 'grid', placeItems: 'center', inlineSize: 40, blockSize: 40, borderRadius: '50%',
                        background: ACCENT_SOFT, color: ACCENT }}>
                        <Icon size={18} />
                      </span>
                    </div>

                    <div style={{ display: 'grid', gap: 2 }}>
                      <h3 style={{ fontSize: '1rem', fontWeight: 700, color: SYSTEM_COLORS.BLACK.DARK_NAVY, margin: 0 }}>{card.title}</h3>
                      <p style={{ ...sectionSubtitleStyle, fontSize: '0.75rem', minBlockSize: '2.4em' }}>{card.description}</p>
                    </div>

                    <div style={{ display: 'grid', gap: 2 }}>
                      <strong style={{ fontSize: '1.4rem', fontWeight: 800, color: SYSTEM_COLORS.BLACK.DARK_NAVY }}>
                        {formatCompactCurrency(card.totalValue)}
                      </strong>
                      <span style={{ fontSize: '0.75rem', color: SYSTEM_COLORS.GRAY.MEDIUM_GRAY }}>
                        {card.totalOfOpportunities} oportunidades · {formatCompactCurrency(card.capturedValue)} capturado
                      </span>
                    </div>

                    <div style={{ blockSize: 6, borderRadius: 999, background: SYSTEM_COLORS.GRAY.ZINC_100 }}>
                      <div style={{
                        blockSize: '100%', borderRadius: 999, background: ACCENT,
                        inlineSize: `${Math.min(card.capturedPercent, 1) * 100}%` }} />
                    </div>

                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: ACCENT }}>
                      {formatPercent(card.capturedPercent)} capturado
                    </span>
                  </div>

                  {!card.isAvailable && <ComingSoonBadge />}
                </article>
              );
            })}
          </div>

          {selectedCard && (
            <div style={{
              padding: 24, borderRadius: 20, display: 'grid', gap: 16,
              background: '#f3ecea', border: '1px solid rgba(224,98,58,0.3)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 700, color: SYSTEM_COLORS.BLACK.DARK_NAVY, margin: 0 }}>
                  Destaques de {selectedCard.title}
                </h3>
                <button
                  type="button"
                  onClick={() => navigate(selectedCard.route)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 18px', border: 'none',
                    borderRadius: 999, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 700,
                    color: SYSTEM_COLORS.WHITE.WHITE, background: ACCENT }}>
                  Ver oportunidades <ArrowRight size={14} />
                </button>
              </div>

              {selectedCard.highlights.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
                {selectedCard.highlights.map(highlight => (
                  <div
                    key={highlight.title}
                    style={{
                      display: 'grid', gridTemplateColumns: '1fr auto', columnGap: 12, rowGap: 4, padding: '16px 18px',
                      borderRadius: 16, background: SYSTEM_COLORS.WHITE.WHITE, border: `1px solid ${SYSTEM_COLORS.GRAY.GRAY_200}` }}>
                    <span style={{ fontSize: '0.95rem', fontWeight: 600, color: SYSTEM_COLORS.BLACK.DARK_NAVY }}>{highlight.title}</span>
                    <strong style={{ fontSize: '0.95rem', fontWeight: 700, color: ACCENT }}>{formatCompactCurrency(highlight.value)}</strong>
                    <span style={{ gridColumn: '1 / -1', fontSize: '0.8rem', color: SYSTEM_COLORS.GRAY.MEDIUM_GRAY }}>
                      {highlight.description}
                    </span>
                  </div>
                ))}
              </div>
              )}
            </div>
          )}
        </section>

        {/* Recomendações da IA */}
        <section style={{ ...cardStyle, padding: 24, display: 'grid', gap: 20 }}>
          <div style={{ display: 'grid', gap: 4 }}>
            <h2 style={{ ...sectionTitleStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Sparkles size={18} color={ACCENT} /> Próximas ações priorizadas pela IA
            </h2>
            <p style={sectionSubtitleStyle}>Recomendações cruzando demanda, estoque, preço e capacidade de execução</p>
            {productWithGreatestOpportunityValue && (
              <p style={sectionSubtitleStyle}>
                Maior oportunidade em valor: <strong style={{ color: SYSTEM_COLORS.GRAY.CHARCOAL_GRAY }}>{productWithGreatestOpportunityValue}</strong>
              </p>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 16 }}>
            {/* sem dados da API: esqueletos com blur, sem conteúdo inventado */}
            {!recommendations.length && [1, 2].map(index => (
              <div
                key={index}
                style={{
                  display: 'grid', gap: 10, padding: '18px 20px', borderRadius: 16, ...blurredStyle,
                  background: SYSTEM_COLORS.GRAY.ZINC_100, border: '1px solid transparent' }}>
                <span style={{ inlineSize: '55%', blockSize: 14, borderRadius: 6, background: SYSTEM_COLORS.GRAY.COOL_GRAY }} />
                <span style={{ inlineSize: '90%', blockSize: 10, borderRadius: 6, background: SYSTEM_COLORS.GRAY.GRAY_200 }} />
                <span style={{ inlineSize: '70%', blockSize: 10, borderRadius: 6, background: SYSTEM_COLORS.GRAY.GRAY_200 }} />
                <span style={{ inlineSize: '25%', blockSize: 10, borderRadius: 6, background: SYSTEM_COLORS.GRAY.COOL_GRAY }} />
              </div>
            ))}

            {recommendations.map(item => (
              <article
                key={item.id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(item.route)}
                onKeyDown={(e) => e.key === 'Enter' && navigate(item.route)}
                onMouseEnter={() => setHoveredId(item.id)}
                onMouseLeave={() => setHoveredId(null)}
                style={{
                  position: 'relative', padding: '18px 20px', borderRadius: 16, outline: 'none', boxShadow: 'none',
                  cursor: 'pointer',
                  // borda transparente reserva o espaço para o hover não deslocar o layout
                  background: SYSTEM_COLORS.GRAY.ZINC_100, border: '1px solid transparent',
                  transition: 'border-color .15s, background-color .15s',
                  // usar sempre o shorthand `border`: misturar com `borderColor` faz o React limpar a cor ao sair do hover
                  ...(hoveredId === item.id && { border: '1px solid rgba(224,98,58,0.45)', background: '#fdf6f3' }) }}>
                <div style={{
                  display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 14, rowGap: 6 }}>
                  <span style={{
                    inlineSize: 10, blockSize: 10, borderRadius: '50%', marginBlockStart: 6,
                    background: SEVERITY_COLORS[item.severity] }} />
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                    <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: SYSTEM_COLORS.BLACK.DARK_NAVY, margin: 0 }}>
                      {item.title}
                    </h3>
                    <span style={{
                      padding: '2px 8px', borderRadius: 999, fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.06em',
                      color: SYSTEM_COLORS.GRAY.DAVY_GRAY, background: SYSTEM_COLORS.GRAY.GRAY_200 }}>
                      {item.moduleLabel.toUpperCase()}
                    </span>
                  </div>

                  <p style={{ gridColumn: 2, margin: 0, fontSize: '0.8rem', lineHeight: 1.6, color: SYSTEM_COLORS.GRAY.CHARCOAL_GRAY }}>
                    {item.description}
                  </p>

                  <span style={{
                    gridColumn: 2, justifySelf: 'start', display: 'inline-flex', alignItems: 'center', gap: 6,
                    fontSize: '0.8rem', fontWeight: 700, color: ACCENT }}>
                    {item.impactLabel} <ArrowRight size={14} />
                  </span>
                </div>
              </article>
            ))}
          </div>
        </section>
      </section>
    </Default>
  );
}
